import { useState, type FC } from "react";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import Modal from "../Modal";
import { Row } from "react-grid-system";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ActionButton from "../../atoms/buttons/ActionButton";
import Alert from "../Alert";
import { FixIconSmall } from "../../atoms/icons/FixIcon";
import type { InvoiceServiceType } from "../../../models/InvoiceServiceType";
import { useUpdateInvoiceDocumentUploadDocumentServiceDetails } from "../../../graphql/hooks/documentHandler/useUpdateInvoiceUploadDocumentServiceDetails";
import TextField from "../../atoms/inputs/TextField";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";
import { useTranslation } from "react-i18next";
import CircleButton from "../../atoms/buttons/CircleButton";
import { PlusIcon } from "../../atoms/icons/PlusIcon";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import BodySmall from "../../atoms/typography/BodySmall";

interface Props {
  documentId: string;
  serviceTypeInitial?: InvoiceServiceType;
  recipientGlnInitial?: string;
  recipientEmailsInitial?: string[];
}

const UpdateDocumentSettingsButton: FC<Props> = ({
  documentId,
  serviceTypeInitial,
  recipientGlnInitial,
  recipientEmailsInitial,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [serviceType, setServiceType] = useState<
    InvoiceServiceType | undefined
  >(serviceTypeInitial);
  const [gln, setGln] = useState<string | undefined>(recipientGlnInitial);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [recipientEmails, setRecipientEmails] = useState<string[]>([
    ...(recipientEmailsInitial || []),
  ]);

  const onCompleted = (response: any) => {
    if (
      response?.documentHandler?.updateInvoiceUploadDocumentServiceDetails
        ?.success === true
    ) {
      setErrorMessage("");
      setSuccessMessage(t("documentHandler.documentHasBeeenUpdated"));
    } else {
      setErrorMessage(
        t("documentHandler.weWereUnableToUpdateTheDocumentSettings"),
      );
    }
  };
  const onError = () => {
    setErrorMessage(t("errors.somethingWentWrong"));
  };
  const { updateServiceDetails, error, loading } =
    useUpdateInvoiceDocumentUploadDocumentServiceDetails({
      onCompleted,
      onError,
    });

  const onClickUpdateServiceType = async () => {
    if (!serviceType) {
      setErrorMessage(t("documentHandler.mustSelectValidServiceType"));
    } else {
      try {
        await updateServiceDetails({
          variables: {
            input: {
              uploadDocumentId: documentId,
              serviceType: serviceType,
              recipientEmails: recipientEmails,
              recipientGln: gln,
            },
          },
        });
      } catch (e) {
        setErrorMessage(`${t("errors.somethingWentWrong")}, ${e?.toString()}`);
      }
    }
  };

  const onClickUpdateDocumentSettingsButton = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <div>
      <Tooltip
        content={
          <Body color={theme.colors.white}>
            {t("documentHandler.updateDocumentSettings")}
          </Body>
        }
      >
        <IconButton
          icon={<FixIconSmall color={theme.colors.warning} />}
          color={theme.colors.warning20}
          onClick={onClickUpdateDocumentSettingsButton}
          size="medium"
        />
      </Tooltip>

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          width={500}
          title={t("documentHandler.updateDocumentSettings")}
        >
          <div>
            <Row justify="center">
              <Body>{t("documentHandler.selectServiceType")}</Body>
            </Row>
            <HorizontalSpacer spacing={0.5} />
            <Row nogutter align="center" justify="around">
              <RadioButtonBox
                title={t("documentHandler.adminOnly")}
                checked={serviceType === "ADMINISTER_INVOICE"}
                onChange={() => setServiceType("ADMINISTER_INVOICE")}
                disabled={successMessage.length > 0}
              />
              <RadioButtonBox
                title={t("documentHandler.sellInvoice")}
                checked={serviceType === "SELL_INVOICE"}
                onChange={() => setServiceType("SELL_INVOICE")}
                disabled={successMessage.length > 0}
              />
            </Row>
          </div>
          <HorizontalSpacer spacing={2} />

          <div>
            <TextField
              label={t("common.gln")}
              value={gln}
              onChange={(value) => setGln(value)}
              placeholder={t("documentHandler.enterRecipientGln")}
              optional
              disabled={successMessage.length > 0}
              fullWidth
            />
          </div>

          <HorizontalSpacer spacing={1} />
          {recipientEmails.length < 1 && (
            <BodySmall>
              {t("documentHandler.clickThePlusButtonToAddRecipientEmails")}
            </BodySmall>
          )}
          {recipientEmails.map((recipientEmail, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                marginBottom: 8,
              }}
            >
              <TextField
                label={`${t("common.email")} ${index + 1}`}
                value={recipientEmail}
                onChange={(value) => {
                  const newRecipientEmails = [...recipientEmails];
                  newRecipientEmails[index] = value;
                  setRecipientEmails(newRecipientEmails);
                }}
                placeholder={t("documentHandler.enterRecipientEmail")}
                optional
                disabled={successMessage.length > 0}
                fullWidth
              />
              <CircleButton
                icon={<TrashIcon color={theme.colors.error} />}
                onClick={() => {
                  const newRecipientEmails = recipientEmails.filter(
                    (_, i) => i !== index,
                  );
                  setRecipientEmails(newRecipientEmails);
                }}
                disabled={successMessage.length > 0}
                color={theme.colors.error20}
              />
            </div>
          ))}
          <HorizontalSpacer spacing={0.5} />
          <Row justify="end" nogutter>
            <CircleButton
              icon={<PlusIcon color={theme.colors.primary} />}
              onClick={() => setRecipientEmails([...recipientEmails, ""])}
              disabled={recipientEmails.length > 0}
            />
          </Row>
          <HorizontalSpacer spacing={1} />

          <HorizontalSpacer spacing={1} />

          {errorMessage ||
            (error && (
              <Alert
                type="error"
                title={t(
                  "documentHandler.weWereUnableToUpdateTheDocumentSettings",
                )}
                message={errorMessage || error?.toString()}
              />
            ))}

          {successMessage && <Alert title={successMessage} type="success" />}

          <HorizontalSpacer spacing={1} />

          <Row justify="center">
            <ActionButton
              title={t("documentHandler.updateDocumentSettings")}
              loading={loading}
              onClick={onClickUpdateServiceType}
              disabled={!serviceType || loading || successMessage.length > 0}
              color={theme.colors.primary}
              icon={<FixIconSmall color={theme.colors.white} />}
            />
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default UpdateDocumentSettingsButton;
